import { ForwardedRef, useId } from "react";
import { useSelect } from "downshift";
import { useDesignTokens } from "@boi/react-design-tokens";
import { forwardRefGenerics } from "@boi/react-utils";

import * as S from "./MultiSelect.styled";
import type { MultiSelectItem } from "./MultiSelect.types";
import { MAX_LIST_HEIGHT } from "./MultiSelect.constants";
import { itemToString, stateReducer, getButtonText } from "./MultiSelect.utils";
import { InputWrapper } from "../InputWrapper";
import { InputAlerts, type InputAlertsProps } from "../InputAlerts";
import { InputLabelTop, type InputLabelTopProps } from "../InputLabelTop";
import { SelectBase, type SelectBaseProps } from "../SelectBase";
import { Typography } from "../Typography";
import { CheckboxBase } from "../CheckboxBase";
import { buildAlertAssociationProps } from "../Alert/AlertAssociationProps";

export type MultiSelectProps<T extends MultiSelectItem = MultiSelectItem> =
  Omit<
    SelectBaseProps<T>,
    | "toggleButtonProps"
    | "getToggleButtonProps"
    | "getMenuProps"
    | "isOpen"
    | "renderButtonContent"
    | "renderListItem"
    | "maxListHeight"
  > & {
    labelProps: Omit<
      InputLabelTopProps,
      "id" | "htmlFor" | "onClick" | "labelHintID" | "children"
    >;
    heading: string;
    selectedItems: T[];
    onChange: (selectedItems: T[]) => void;
    alertProps?:
      | InputAlertsProps["alerts"][number]
      | InputAlertsProps["alerts"];
  };

function MultiSelect<T extends MultiSelectItem = MultiSelectItem>(
  props: MultiSelectProps<T>,
  ref: ForwardedRef<HTMLDivElement>
) {
  const {
    labelProps,
    heading,
    selectedItems,
    onChange,
    alertProps,
    testID = "multi-select",
    ...selectBaseProps
  } = props;

  const { items } = selectBaseProps;

  const { tokens } = useDesignTokens();
  const { multiSelect } = tokens;

  const {
    getLabelProps,
    getToggleButtonProps,
    getMenuProps,
    isOpen,
    getItemProps,
    highlightedIndex,
  } = useSelect({
    items,
    itemToString,
    selectedItem: null,
    stateReducer,
    onSelectedItemChange: ({ selectedItem }) => {
      if (!selectedItem) {
        return;
      }

      const index = selectedItems.indexOf(selectedItem);

      if (index > 0) {
        onChange([
          ...selectedItems.slice(0, index),
          ...selectedItems.slice(index + 1),
        ]);
      } else if (index === 0) {
        onChange([...selectedItems.slice(1)]);
      } else {
        onChange([...selectedItems, selectedItem]);
      }
    },
  });

  const labelHintID = useId();
  const { labelHint } = labelProps;
  const safeLabelHintID = labelHint ? labelHintID : undefined;

  const alertId = useId();
  const ariaAlertAssociationProps = buildAlertAssociationProps(
    alertId,
    props,
    safeLabelHintID
  );

  return (
    <div ref={ref}>
      <InputWrapper
        alert={
          alertProps && (
            <div id={alertId} data-testid={`${testID}-alerts`}>
              <InputAlerts
                alerts={Array.isArray(alertProps) ? alertProps : [alertProps]}
              />
            </div>
          )
        }
      >
        <InputLabelTop
          {...labelProps}
          {...getLabelProps()}
          labelHintID={labelHintID}
        >
          <SelectBase
            {...selectBaseProps}
            toggleButtonProps={{
              "aria-describedby": safeLabelHintID,
            }}
            getToggleButtonProps={getToggleButtonProps}
            getMenuProps={getMenuProps}
            isOpen={isOpen}
            renderButtonContent={() => (
              <S.ButtonTextContainer>
                <S.ButtonHeadingTextWrapper>
                  <Typography
                    component="div"
                    color="inherit"
                    variant={multiSelect.button.headingText.typography.variant}
                    fontFamily={
                      multiSelect.button.headingText.typography.fontFamily
                    }
                    noWrap={true}
                  >
                    {heading}
                  </Typography>
                </S.ButtonHeadingTextWrapper>

                <S.ButtonSelectedTextWrapper>
                  <Typography
                    component="div"
                    color="inherit"
                    variant={multiSelect.button.selectedText.typography.variant}
                    fontFamily={
                      multiSelect.button.selectedText.typography.fontFamily
                    }
                    noWrap={true}
                  >
                    {getButtonText(items, selectedItems)}
                  </Typography>
                </S.ButtonSelectedTextWrapper>
              </S.ButtonTextContainer>
            )}
            renderListItem={(item, index) => {
              const isSelected = selectedItems.includes(item);
              const isHighlighted = highlightedIndex === index;

              return (
                <S.ListItem
                  key={item.id}
                  {...getItemProps({
                    item,
                    index,
                    "aria-selected": isSelected,
                  })}
                  data-hovered={isHighlighted || undefined}
                  // HACK: this tabIndex=-1 makes talkback in chrome work
                  // Chromium issue: https://issues.chromium.org/issues/40260928
                  tabIndex={-1}
                >
                  <S.ListItemContentWrapper
                    $isSelected={isSelected}
                    $isHighlighted={isHighlighted}
                  >
                    <S.ListItemContent>
                      <S.ListItemCheckboxWrapper>
                        <CheckboxBase
                          {...{ inert: "" }}
                          value={item.title}
                          checked={isSelected}
                          onChange={() => null}
                        />
                      </S.ListItemCheckboxWrapper>

                      <Typography
                        component="div"
                        color="inherit"
                        title={item.title}
                        variant={
                          multiSelect.menu.option.inner.text.typography.variant
                        }
                        fontFamily={
                          multiSelect.menu.option.inner.text.typography
                            .fontFamily
                        }
                      >
                        {item.title}
                      </Typography>
                    </S.ListItemContent>
                  </S.ListItemContentWrapper>
                </S.ListItem>
              );
            }}
            maxListHeight={MAX_LIST_HEIGHT}
            testID={testID}
            alertAssociationProps={ariaAlertAssociationProps}
          />
        </InputLabelTop>
      </InputWrapper>
    </div>
  );
}

export default forwardRefGenerics(MultiSelect);
